
import { 
  Waves, GlassWater, Utensils, Train, Wifi, Car, 
  Dumbbell, Baby, Heart, Plane, MapPin, Coffee,
  Wind, Sun, Mountain, TreePine, UtensilsCrossed,
  Gamepad2, Music, Camera, Shirt, Shield
} from "lucide-react";

export interface Facility {
  label: string;
  icon: any;
  priority: number;
}

export const facilityMapping: Record<string, Facility> = {
  swimming_pool: {
    label: "Zwembad",
    icon: Waves,
    priority: 1
  },
  bar: {
    label: "Bar",
    icon: GlassWater,
    priority: 2
  },
  restaurant: {
    label: "Restaurant",
    icon: Utensils,
    priority: 3
  },
  train_included: {
    label: "Trein inbegrepen",
    icon: Train,
    priority: 4
  },
  wifi: {
    label: "WiFi",
    icon: Wifi,
    priority: 5
  },
  parking: {
    label: "Parkeren",
    icon: Car,
    priority: 6
  },
  fitness: {
    label: "Fitness",
    icon: Dumbbell,
    priority: 7
  },
  kids_club: {
    label: "Kinderclub",
    icon: Baby,
    priority: 8
  },
  spa: {
    label: "Spa/Wellness",
    icon: Heart,
    priority: 9
  },
  airport_transfer: {
    label: "Luchthaven transfer",
    icon: Plane,
    priority: 10
  },
  beach_access: {
    label: "Strand toegang",
    icon: Sun,
    priority: 11
  },
  mountain_view: {
    label: "Bergzicht",
    icon: Mountain,
    priority: 12
  },
  garden: {
    label: "Tuin",
    icon: TreePine,
    priority: 13
  },
  breakfast: {
    label: "Ontbijt",
    icon: Coffee,
    priority: 14
  },
  air_conditioning: {
    label: "Airconditioning",
    icon: Wind,
    priority: 15
  },
  entertainment: {
    label: "Entertainment",
    icon: Music,
    priority: 16
  },
  excursions: {
    label: "Excursies",
    icon: Camera,
    priority: 17
  },
  laundry: {
    label: "Wasserij",
    icon: Shirt,
    priority: 18
  },
  safe: {
    label: "Kluis",
    icon: Shield,
    priority: 19
  },
  games: {
    label: "Spelletjes",
    icon: Gamepad2,
    priority: 20
  }
};

export const extractFacilities = (vacation: any): Facility[] => {
  const facilities: Facility[] = [];
  
  console.log('Processing vacation facilities:', {
    facilities: vacation.facilities,
    specialCategories: vacation.specialCategories,
    typeAccommodatie: vacation.typeAccommodatie,
    description: vacation.description
  });
  
  // Check boolean fields first
  if (vacation.swimmingPool === true || vacation.swimmingPool === 'Yes' || vacation.swimmingPool === 'true') {
    facilities.push(facilityMapping.swimming_pool);
  }
  
  if (vacation.trainIncluded === true || vacation.trainIncluded === 'Yes' || vacation.trainIncluded === 'true') {
    facilities.push(facilityMapping.train_included);
  }
  
  // Parse facilities string field - look for various patterns
  const textFields = [
    vacation.facilities,
    vacation.specialCategories,
    vacation.description,
    vacation.typeAccommodatie
  ].filter(field => field && typeof field === 'string');
  
  const combinedText = textFields.join(' ').toLowerCase();
  
  // Enhanced facility detection
  const facilityPatterns = {
    swimming_pool: ['zwembad', 'pool', 'swimming', 'zwem', 'binnenzwembad', 'buitenzwembad'],
    bar: ['bar', 'lobby bar', 'poolbar', 'strandbar'],
    restaurant: ['restaurant', 'dining', 'eten', 'buffet', 'a la carte', 'diner'],
    train_included: ['trein', 'train', 'transport', 'vervoer inbegrepen'],
    wifi: ['wifi', 'wi-fi', 'internet', 'gratis wifi'],
    parking: ['parkeren', 'parking', 'gratis parkeren', 'parkeerplaats'],
    fitness: ['fitness', 'gym', 'sportschool', 'fitnessruimte'],
    kids_club: ['kinderclub', 'kids club', 'miniclub', 'kinderen', 'kindvriendelijk'],
    spa: ['spa', 'wellness', 'sauna', 'massage', 'beauty'],
    airport_transfer: ['luchthaven', 'airport', 'transfer', 'shuttle'],
    beach_access: ['strand', 'beach', 'zee', 'kust', 'strandtoegang'],
    mountain_view: ['berg', 'mountain', 'uitzicht', 'view', 'panorama'],
    garden: ['tuin', 'garden', 'groen', 'natuur'],
    breakfast: ['ontbijt', 'breakfast', 'buffet', 'morgenmaal'],
    air_conditioning: ['airco', 'air conditioning', 'airconditioning', 'klimaat'],
    entertainment: ['entertainment', 'animatie', 'show', 'muziek', 'dans'],
    excursions: ['excursie', 'uitstap', 'tour', 'rondleiding'],
    laundry: ['wasserij', 'laundry', 'was', 'schoonmaak'],
    safe: ['kluis', 'safe', 'veilig', 'bewaren'],
    games: ['spel', 'game', 'biljart', 'tafeltennis', 'activiteit']
  };
  
  // Check each facility pattern
  Object.entries(facilityPatterns).forEach(([facilityKey, patterns]) => {
    const matchesPattern = patterns.some(pattern => 
      combinedText.includes(pattern)
    );
    
    if (matchesPattern) {
      const facility = facilityMapping[facilityKey];
      if (facility) {
        const exists = facilities.some(f => f.label === facility.label);
        if (!exists) {
          facilities.push(facility);
        }
      }
    }
  });
  
  // Add some facilities based on vacation properties for better variety
  if (vacation.all_inclusive === 'Yes') {
    // All-inclusive resorts usually have bars and restaurants
    ['bar', 'restaurant'].forEach(facilityKey => {
      const facility = facilityMapping[facilityKey];
      if (facility) {
        const exists = facilities.some(f => f.label === facility.label);
        if (!exists && Math.random() > 0.3) { // 70% chance
          facilities.push(facility);
        }
      }
    });
  }
  
  // Beach destinations likely have beach access
  if (vacation.country && ['Spanje', 'Griekenland', 'Turkije', 'Egypte', 'Tunesië'].includes(vacation.country)) {
    const facility = facilityMapping.beach_access;
    if (facility) {
      const exists = facilities.some(f => f.label === facility.label);
      if (!exists && Math.random() > 0.5) { // 50% chance
        facilities.push(facility);
      }
    }
  }
  
  console.log('Extracted facilities:', facilities.map(f => f.label));
  
  // Remove duplicates and sort by priority
  const uniqueFacilities = facilities.filter((facility, index, self) => 
    index === self.findIndex(f => f.label === facility.label)
  );
  
  return uniqueFacilities.sort((a, b) => a.priority - b.priority);
};
